/**
 * Móveis do pacote do campus, transplantados para o escritório v1: a mesa de
 * reunião, a consola de seis monitores da sala de TI, os bastidores, a máquina
 * central e os ecrãs de parede da sala de automações — e, desde agora, as MOBILE
 * HOMES do parque exterior, que são a razão de o módulo ter deixado de ser só de
 * interiores.
 *
 * O mecanismo é o mesmo do `officeFlora.ts` — e é um mecanismo, não um truque:
 * `texture()` em `officeArt.ts` começa com `if (scene.textures.exists(key)) return;`,
 * por isso QUEM CHEGA PRIMEIRO à chave fica com ela. Este módulo corre antes do
 * `ensureWorldTextures` e regista `prop:meetingTable` e `prop:deskDual` já
 * preenchidas com a arte do pacote; os desenhos em código que as pintavam passam
 * a não-fazer-nada, sozinhos, sem um `if` novo em lado nenhum.
 *
 * A diferença para a flora está no RECORTE. Lá o recorte é automático (a caixa
 * de alfa, «tudo o que não é transparente»). Aqui é escrito à mão, porque nos
 * dois ficheiros parte da arte é para deixar de fora:
 *
 *   - a mesa de reunião vem com oito cadeiras à volta, e essas cadeiras têm de
 *     ser PROPS a sério — gente senta-se nelas (`space.ts`), não são pintura;
 *   - a consola vem com uma cadeira verde colada por baixo, pela mesma razão, e
 *     com armários laterais e rodízios que não cabem numa pegada de uma linha.
 *
 * A caixa de alfa apanharia tudo isso. Os rectângulos abaixo foram medidos nos
 * ficheiros pixel a pixel e depois SIMULADOS em offline (recorte + escala +
 * máscara, ampliado 2×/3×) antes de virem para aqui — não são estimativas.
 */

import type PhaserT from "phaser";

import { LAKE_ART, propKey, propSliceKey, seatPropKey } from "./officeArt";

/** Onde a arte do pacote vive (servida por `apps/web/public`). */
const OBJECTS = "/campus/objects";

/**
 * Chaves de CARREGAMENTO, distintas das chaves de destino.
 *
 * Se um render entrasse directamente em `prop:mobileHome`, o Phaser ficava com a
 * imagem em bruto — 314×302, com margem e sombra — e a casa cobria meia rua. O
 * ficheiro entra por uma porta lateral, é recortado e medido, e só o resultado
 * toma o nome que o mundo desenha. (A mobília nova das salas já vem tratada ao
 * pixel final: a porta lateral serve-lhe só de rede contra o 404, não de tesoura.)
 */
const MESA = "mob:src:mesa-reuniao";
const CONSOLA = "mob:src:consola";
const BASTIDOR = "mob:src:bastidor";
const MAQUINA = "mob:src:maquina";
const PAINEL = "mob:src:painel";
const CASA = "mob:src:mobile-home";
const CASA_LADO = "mob:src:mobile-home-lado";
const CASA_ARCO = "mob:src:mobile-home-arco";
// As quatro casas novas. Uma chave por ficheiro e por casa — sem espelhos, ao
// contrário das três de cima: aqui cada ficheiro É um modelo diferente, e
// espelhar um não daria uma casa nova.
const CASA_DOMO = "mob:src:casa-domo";
const CASA_CAPS_F = "mob:src:casa-capsula-fechada";
const CASA_MODULAR = "mob:src:casa-modular";
const CASA_CAPS = "mob:src:casa-capsula";
const LAGO = "mob:src:lago";
const CASA_LAGO = "mob:src:casa-flutuante";
const SECRETARIA = "mob:src:secretaria-vendas";
// Mobília nova das três salas (cozinha, TI-supervisor, meeting-room). Todos os
// ficheiros abaixo foram preparados em OFFLINE já na medida final de ecrã — a
// mesma que o desenho de recurso produz (`spec.w·2 × spec.h·2`) — por isso o
// `corte` é sempre o ficheiro inteiro, `escalaNitida` devolve k=1 (cópia pixel a
// pixel) e a peça NÃO muda de tamanho se o PNG faltar (fica o desenho do v1).
const COZ_MESA = "mob:src:coz-mesa";
const COZ_CAD_F = "mob:src:coz-cad-frente";
const COZ_CAD_E = "mob:src:coz-cad-esq";
const COZ_CAD_D = "mob:src:coz-cad-dir";
const COZ_CAD_CB = "mob:src:coz-cad-costas-base";
const COZ_CAD_CF = "mob:src:coz-cad-costas-frente";
const TI_SUP = "mob:src:ti-supervisor";
const MTG_QUADRO = "mob:src:mtg-quadro";
const MTG_CAD_D = "mob:src:mtg-cad-down";
const MTG_CAD_E = "mob:src:mtg-cad-esq";
const MTG_CAD_R = "mob:src:mtg-cad-dir";
const MTG_CAD_CB = "mob:src:mtg-cad-costas-base";
const MTG_CAD_CF = "mob:src:mtg-cad-costas-frente";
// Recepção + o cartaz da entrada do parque. Mesmo tratamento offline das linhas
// acima; o que estas três têm de próprio está no comentário de cada peça.
const RECEPCAO = "mob:src:balcao-recepcao";
const BEBEDOURO = "mob:src:bebedouro";
const CARTAZ = "mob:src:cartaz-parque";
// A cadeira ergonómica de malha dos postos de TI e de Vendas. DUAS chaves para
// UM render: o pacote deu a cadeira vista de costas, que é a única vista que o
// escritório desenha (as nove `officeChair` do mapa sentam-se todas viradas a
// norte), e essa vista parte-se em base/frente como as outras cadeiras.
const CAD_ESC_B = "mob:src:cadeira-escritorio-base";
const CAD_ESC_F = "mob:src:cadeira-escritorio-frente";
// Os quatro carros das baias do parque. As chaves dizem a SILHUETA (é o que o
// mapa conhece), o ficheiro diz o MODELO — a arte pode ser trocada sem que
// `space.ts` saiba de marcas nenhumas.
// A estante e o sofá. Os dois renders chegaram com o XADREZ DE TRANSPARÊNCIA
// PINTADO no RGB — alfa 255 em todos os pixels, o "nada" era cor a sério. Cortar
// pela caixa de alfa dava o quadro inteiro, e reduzir assim trazia o xadrez para
// dentro do jogo. Foram limpos em offline por alagamento a partir da moldura (só
// sai o claro-neutro LIGADO à borda, para os livros claros e os realces do sofá
// sobreviverem) e depois ficou só a maior componente ligada, que varre as
// manchas de marca-de-água soltas — na estante eram 90 ilhas de 123 px no total
// que, sozinhas, esticavam a caixa de 899×747 para 965×908.
const PRATELEIRA = "mob:src:prateleira";
const RELOGIO = "mob:src:relogio-de-pe";
const SOFA = "mob:src:sofa";
// O sofá de PERFIL da sala de TI, em três ficheiros — um por fatia. É a única
// peça deste módulo com mais do que uma imagem para o MESMO móvel, e a razão não
// é artística: `couchSide` tem pegada 1×3 e o y-sort da cena dá uma profundidade
// por imagem, por isso um sofá inteiro numa imagem só tapava duas das três
// pessoas sentadas nele (ver `drawCouchSide` em `officeArt.ts`).
//
// O alvo destas três peças não é `propKey("couchSide")` mas sim
// `propSliceKey("couchSide", i)` — as mesmas chaves que o desenho em código
// gera. Vale porque o `alvo` é uma string qualquer: o `ensureFurnitureTextures`
// corre ANTES do `ensureWorldTextures` e a fatia desenhada só nasce se o PNG
// respectivo faltar. Trocar o sofá é trocar os três ficheiros, nunca um só.
const SOFA_LAT_0 = "mob:src:sofa-lateral-0";
const SOFA_LAT_1 = "mob:src:sofa-lateral-1";
const SOFA_LAT_2 = "mob:src:sofa-lateral-2";
// A televisão da Sala de Descanso, vista de TRÁS — e isso não é um engano do
// render, é a única vista correcta ali. O sofá está na row 18 com o encosto a
// norte (quem lá se senta olha para SUL), a mesa de café na 20 e a TV na 22,
// encostada à parede sul a olhar para o sofá. Ou seja: a câmara do jogo vê as
// COSTAS do aparelho. O desenho em código que aqui estava mostrava um ecrã aceso
// com paisagem e passarinhos — bonito e virado ao contrário.
const TV = "mob:src:tv";
/**
 * O helicóptero pousado no heliponto do telhado. Não é peça do mapa (não tem
 * pegada nem recorte): a cena desenha-o directamente com esta chave.
 */
export const HELICOPTERO = "mob:src:helicoptero";
/** O mesmo helicóptero SEM as pás — o `officeHeli` desenha-as, a rodar. */
export const HELICOPTERO_CORPO = "mob:src:helicoptero-corpo";
/** O pato do lago, virado para a direita (o `officeVida` espelha-o). */
export const PATO = "mob:src:pato";
// Os eletrodomésticos da Copa. DUAS chaves para UM ficheiro, como as mobile
// homes de lado — mas aqui não é por espelho, é por RECORTE: o render é uma
// bancada corrida com o frigorífico encostado à direita, e no mapa isso são dois
// props com pegadas próprias (`counter` 4×1, `fridge` 1×1) e colisões próprias.
const COZ_BANCADA = "mob:src:coz-bancada";
const COZ_FRIGORIFICO = "mob:src:coz-frigorifico";
const CARRO_DESP = "mob:src:carro-desportivo"; // Ferrari SF90 Stradale
const CARRO_SEDAN = "mob:src:carro-sedan"; // Bentley Continental GT Speed
const CARRO_SUPER = "mob:src:carro-super"; // Lamborghini Revuelto
const CARRO_SUV = "mob:src:carro-suv"; // Rolls-Royce Cullinan Black Badge
// Os três objectos de cenário do estacionamento das mobile homes. Chegaram como
// renders enormes (1485×1059, 1254×1254, 1223×1286) e com DOIS problemas cozidos
// que nenhum deles anuncia — ver o comentário do bloco em `FICHEIROS`.
const CACAMBA = "mob:src:cacamba";
const LIXEIRA = "mob:src:lixeira";
const CONE = "mob:src:cone";

const FICHEIROS: Record<string, string> = {
  [MESA]: `${OBJECTS}/indoor/meeting-table-v1.png`,
  [CONSOLA]: `${OBJECTS}/indoor/ti-desk-v1.png`,
  [BASTIDOR]: `${OBJECTS}/indoor/server-rack-v1.png`,
  [MAQUINA]: `${OBJECTS}/indoor/ai-console-v1.png`,
  [PAINEL]: `${OBJECTS}/indoor/analytics-screen-v1.png`,
  [CASA]: `${OBJECTS}/outdoor/mobile-home-v1.png`,
  // Estes dois entram JÁ recortados e JÁ no tamanho de ecrã — 192×66 e 160×114,
  // sem margem e sem fundo. A `v1` chegou como render de 314×302 e por isso
  // precisa de `corte` e de encolher para 0,54×; estas foram preparadas ao pixel
  // final de propósito, para o `recortar` as copiar a 1:1 (ver `escalaNitida`).
  [CASA_LADO]: `${OBJECTS}/outdoor/mobile-home-v2.png`,
  [CASA_ARCO]: `${OBJECTS}/outdoor/mobile-home-v3.png`,
  // ── As quatro casas novas, assadas em offline a partir dos renders do ambiente
  // de trabalho. Cada uma sai já na medida exacta de ecrã (pegada × 32 na largura),
  // e isso não foi conveniência: os originais são de 1100 a 1500 px de caixa opaca
  // para 160-224 px finais, ou seja reduções de 0,111× a 0,159×. O `recortar` faz
  // a redução com UM `drawImage`, que abaixo de 0,5× salta linhas de pixels — a
  // casa chegava ao ecrã com o telhado às tiras. Reduzir em offline, com filtro
  // de caixa sobre alfa PRÉ-MULTIPLICADO, é a única forma honesta de descer uma
  // ordem de grandeza; feito assim, o `escalaNitida` devolve k=1 e o recorte é
  // uma cópia pixel a pixel.
  //
  // Os quatro renders não vieram todos iguais: uns já traziam alfa, outros vinham
  // opacos de ponta a ponta com um fundo quase-branco RUIDOSO (242-255, não uma cor
  // chapada). Nesses o fundo saiu por alagamento a partir da moldura, a seguir o
  // gradiente com tolerância local — um teste global de cor comia o branco da
  // própria casa. Nenhuma traz sombra de contacto pintada (confirmado a olho sobre
  // magenta), o que importa porque o motor desenha a SUA: uma sombra cozida
  // duplicava-se numa laje cinzenta debaixo da casa.
  [CASA_DOMO]: `${OBJECTS}/outdoor/home-dome-v1.png`,
  [CASA_CAPS_F]: `${OBJECTS}/outdoor/home-capsule-closed-v1.png`,
  [CASA_MODULAR]: `${OBJECTS}/outdoor/home-modular-v1.png`,
  [CASA_CAPS]: `${OBJECTS}/outdoor/home-capsule-v1.png`,
  // O lago e a casa que assenta nele. Vêm pela mesma porta e pela mesma razão:
  // este módulo é o ÚNICO sítio do escritório que já sabe distinguir «o PNG não
  // chegou» de «o PNG chegou vazio» (ver `falhadas`). O lago não é um móvel, mas
  // o problema dele é o mesmo — se o ficheiro faltar tem de haver plano B, e um
  // segundo caminho de carregamento dentro do `OfficeSpace.tsx` seria um segundo
  // sítio onde esquecer a armadilha do 404 silencioso.
  [LAGO]: `${OBJECTS}/outdoor/lake.png`,
  [CASA_LAGO]: `${OBJECTS}/outdoor/floating-house.png`,
  // Os quatro carros. Vistas de cima ortográficas, 64×128 cada, alfa limpo e
  // sem sombra cozida — medido ficheiro a ficheiro antes de entrarem: a caixa
  // opaca é de 50 a 58 px de largura para 120 de altura, ou seja a carroçaria
  // deixa margem transparente de ambos os lados e NUNCA transborda a pegada.
  // São o quarto caso de ficheiro preparado ao pixel final: 64×128 é exactamente
  // 2×4 tiles, o `recortar` copia-os a 1:1 e o `escalaNitida` dá vizinho-mais-
  // próximo. Redimensionar um destes seria estragá-los de graça.
  [CARRO_DESP]: `${OBJECTS}/outdoor/car-sport-v1.png`,
  [CARRO_SEDAN]: `${OBJECTS}/outdoor/car-sedan-v1.png`,
  [CARRO_SUPER]: `${OBJECTS}/outdoor/car-super-v1.png`,
  [CARRO_SUV]: `${OBJECTS}/outdoor/car-suv-v1.png`,
  // Os três objectos de cenário que acompanham os carros no estacionamento. Vêm
  // preparados ao pixel final (64×56, 32×48, 32×40) porque reduzir 1485→64 é
  // 0,070× — e 0,052× e 0,038× nos outros dois. Isso é uma ordem de grandeza
  // abaixo do meio-tamanho a partir do qual o `drawImage` do `recortar` salta
  // linhas: feito no browser saíam três borrões. Filtro de caixa em alfa
  // pré-multiplicado, uma única passagem, em offline.
  //
  // Dois problemas vinham cozidos no render e tiveram de sair ANTES do PNG
  // entrar aqui, porque nenhum deles se resolve em runtime:
  //  1. o «fundo transparente» dos dois primeiros era o XADREZ PINTADO em RGB
  //     (alfa 255 em todo o lado), como já tinha acontecido na prateleira, no
  //     sofá e na TV. Enchimento a partir da MOLDURA com tolerância — nunca um
  //     teste global de cor, que comeria a etiqueta branca da reciclagem — e
  //     depois só a maior ilha ligada sobrevive;
  //  2. a SOMBRA de contacto vinha desenhada. O `OfficeSpace` já põe a sua
  //     elipse por baixo de CADA prop, portanto uma sombra pintada duplicava
  //     num tapete cinzento. E não se separa por luminância: a sombra da
  //     lixeira anda nos ~150 e o metal dela nos ~140. Separa-se por
  //     TEMPERATURA — a sombra é quente (155,148,144: r ≥ b+5), o metal é frio
  //     (139,144,154: b > r). No cone a sombra até veio em alfa a sério, e essa
  //     saiu por outra porta: pixels semi-transparentes quase pretos.
  [CACAMBA]: `${OBJECTS}/outdoor/dumpster-v1.png`,
  [LIXEIRA]: `${OBJECTS}/outdoor/trash-can-v1.png`,
  [CONE]: `${OBJECTS}/outdoor/traffic-cone-v1.png`,
  // Terceiro ficheiro preparado ao pixel final (64×46), pela mesma razão que as
  // duas mobile homes acima: o render de origem tinha 1448×1086 e chegar aos 64
  // é reduzir a 0,05×, uma ordem de grandeza abaixo do meio-tamanho a partir do
  // qual o `drawImage` deste ficheiro começa a saltar linhas de pixels. Reduzido
  // uma vez, em offline, com filtro de caixa em alfa pré-multiplicado.
  [SECRETARIA]: `${OBJECTS}/indoor/sales-desk-v1.png`,
  // Cozinha/Copa: mesa + quatro vistas de cadeira (a de costas parte-se em
  // base/frente para o encosto passar à frente de quem se senta).
  [COZ_MESA]: `${OBJECTS}/indoor/kitchen-table-v1.png`,
  [COZ_CAD_F]: `${OBJECTS}/indoor/dining-chair-front-v1.png`,
  [COZ_CAD_E]: `${OBJECTS}/indoor/dining-chair-left-v1.png`,
  [COZ_CAD_D]: `${OBJECTS}/indoor/dining-chair-right-v1.png`,
  [COZ_CAD_CB]: `${OBJECTS}/indoor/dining-chair-back-base-v1.png`,
  [COZ_CAD_CF]: `${OBJECTS}/indoor/dining-chair-back-front-v1.png`,
  // Bancada e frigorífico: o render veio inteiro (1774×887) e foi partido em
  // offline na coluna x=1368, que não é um palpite — é onde o perfil de topo da
  // imagem salta de y=321 para y=171, ou seja a coluna exacta em que o tampo da
  // bancada acaba e o frigorífico, que é mais alto, começa. Cada metade saiu com
  // a sua caixa de alfa e à largura da sua pegada.
  [COZ_BANCADA]: `${OBJECTS}/indoor/kitchen-counter-v1.png`,
  [COZ_FRIGORIFICO]: `${OBJECTS}/indoor/kitchen-fridge-v1.png`,
  // Sala de TI: só o supervisor entra aqui (as secretárias dos funcionários
  // repontam a `CONSOLA` para `ti-desk-v1` acima).
  [TI_SUP]: `${OBJECTS}/indoor/ti-supervisor-desk-v1.png`,
  // Meeting-room: quadro branco + cadeiras (mesma partição de costas da cozinha).
  [MTG_QUADRO]: `${OBJECTS}/indoor/meeting-board-v1.png`,
  [MTG_CAD_D]: `${OBJECTS}/indoor/meeting-chair-front-v1.png`,
  [MTG_CAD_E]: `${OBJECTS}/indoor/meeting-chair-left-v1.png`,
  [MTG_CAD_R]: `${OBJECTS}/indoor/meeting-chair-right-v1.png`,
  [MTG_CAD_CB]: `${OBJECTS}/indoor/meeting-chair-back-base-v1.png`,
  [MTG_CAD_CF]: `${OBJECTS}/indoor/meeting-chair-back-front-v1.png`,
  // Recepção: o balcão e o bebedouro que está ao lado dele.
  [RECEPCAO]: `${OBJECTS}/indoor/reception-desk-v1.png`,
  [BEBEDOURO]: `${OBJECTS}/indoor/water-cooler-v1.png`,
  // Único ficheiro deste bloco que é de EXTERIOR: o cartaz à entrada do parque.
  [CARTAZ]: `${OBJECTS}/outdoor/park-sign-v1.png`,
  // Cadeira de escritório (TI + Vendas). Os dois ficheiros saíram do MESMO
  // render, cortado em offline na linha y=24 de 40 — que é a linha mais estreita
  // da peça, o pescoço entre o assento e a coluna de gás. Somados dão a cadeira
  // inteira ao pixel (verificado: zero pixels em desacordo), que é o que a
  // partição em camadas exige para uma cadeira VAZIA ficar igual à de sempre.
  [CAD_ESC_B]: `${OBJECTS}/indoor/office-chair-back-base-v1.png`,
  [CAD_ESC_F]: `${OBJECTS}/indoor/office-chair-back-front-v1.png`,
  // Estante (Vendas, TI e Sala de Descanso) e sofá (Descanso e Recepção).
  [PRATELEIRA]: `${OBJECTS}/indoor/bookshelf-v1.png`,
  // Relógio de pé (Vendas e TI). Preparado ao pixel final (24×68) em offline: o
  // render tinha 1254×1254 com um halo difuso de alfa à volta; a caixa opaca
  // (alfa > 128, 354×1003) foi reduzida UMA vez com filtro de caixa em alfa
  // pré-multiplicado e o alfa final binarizado, como o resto da pixel art.
  [RELOGIO]: `${OBJECTS}/indoor/grandfather-clock-v1.png`,
  [SOFA]: `${OBJECTS}/indoor/couch-v1.png`,
  // As três fatias do sofá de perfil da TI, cortadas em offline de um composto de
  // 40×124 (ver o bloco de peças lá em baixo). Números no nome porque a ordem é
  // significativa: 0 é a ponta NORTE, 2 a ponta SUL com o braço e a base.
  [SOFA_LAT_0]: `${OBJECTS}/indoor/couch-side-v1-0.png`,
  [SOFA_LAT_1]: `${OBJECTS}/indoor/couch-side-v1-1.png`,
  [SOFA_LAT_2]: `${OBJECTS}/indoor/couch-side-v1-2.png`,
  // Quinto ficheiro preparado ao pixel final (96×62). O render chegou a 1024×523
  // em RGB de 8 bits — sem canal alfa, com o xadrez de transparência PINTADO e já
  // com ruído de compressão à volta (há #fefefe e #c6c6c6 aos milhares junto dos
  // #ffffff/#c5c5c5), por isso o fundo saiu por alagamento a partir das BORDAS com
  // tolerância, e não por comparação de cor: um teste global comia a placa branca
  // da etiqueta, que é desenho cercado pelo aparelho. Sobrou uma caixa opaca de
  // 687×441, reduzida UMA vez em offline com filtro de caixa em alfa
  // pré-multiplicado. 687 → 96 é 0,14×, uma ordem de grandeza abaixo do meio-
  // tamanho a partir do qual o `drawImage` do `recortar` salta linhas — feito no
  // browser saía serrilha, não uma televisão.
  [TV]: `${OBJECTS}/indoor/tv-v1.png`,
  // Helicóptero do heliponto, preparado ao pixel final (256×174) em offline: o
  // render de 1536×1024 já trazia alfa, com um halo dourado quase transparente
  // à volta; a caixa opaca (alfa > 128, 1242×845) foi reduzida uma vez e o alfa
  // final binarizado. Visto de 3/4, de nariz para sudeste, como o resto do mundo.
  [HELICOPTERO]: `${OBJECTS}/outdoor/helicopter-v1.png`,
  // O corpo sem as quatro pás: apagadas no render original numa faixa de 34 px
  // à volta de cada uma e, onde a pá passava por cima da cabine, preenchidas com
  // a cor do corpo logo ao lado dela. Mesma caixa e mesma redução do de cima,
  // por isso os dois PNGs sobrepõem-se ao pixel.
  [HELICOPTERO_CORPO]: `${OBJECTS}/outdoor/helicopter-body-v1.png`,
  // O pato: render de 1536×1024 com alfa e um halo quase transparente; a caixa
  // opaca (577×395) reduzida uma vez a 36×25 e o alfa binarizado.
  [PATO]: `${OBJECTS}/outdoor/duck-v1.png`,
};

/** Rectângulo dentro do ficheiro de origem, em pixels do ficheiro. */
export interface Corte {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Uma troca: a chave que o mundo desenha, o ficheiro, o recorte e a medida final.
 *
 * `w × h` é em pixels de ECRÃ, e não é um gosto: sai da pegada declarada em
 * `PROP_SPECS` (`space.ts`) vezes `TILE = 32`. Os dois valores são um CONTRATO
 * entre os dois ficheiros — mexer num sem mexer no outro desalinha a arte da
 * colisão, e nada avisa.
 */
export interface Peca {
  alvo: string;
  fonte: string;
  corte: Corte;
  /** Largura final, em pixels de ecrã. */
  w: number;
  /** Altura final, em pixels de ecrã. */
  h: number;
  /** Raio do canto arredondado, em pixels finais. 0 = rectângulo. */
  raio: number;
  /**
   * Virar ao espelho no acto de recortar?
   *
   * Não é um efeito: é o que permite que DUAS orientações de mobile home saiam
   * de UM ficheiro. O pacote traz uma casa vista em 3/4, e de uma vista em 3/4
   * só há duas leituras honestas — esta e a espelhada. O campus faz exactamente
   * isto com esta mesma arte (`display-home-left` / `display-home-right`), e é a
   * mesma torneira que o `texture()` de `officeArt.ts` já tinha para as cadeiras
   * viradas para a direita. Espelhar aqui, e não no `add.image`, mantém a peça
   * numa TEXTURA própria: o mundo continua a desenhar props sem saber de nada.
   */
  espelho: boolean;
}

/**
 * Uma cadeira sentável: 32 de largo (um tile), ficheiro inteiro, cópia pixel a
 * pixel (k=1). Cada modelo tem várias chaves — base das vistas down/left/right e
 * base+frente da vista de costas — que só diferem no `alvo` e na `fonte`, por
 * isso partilham esta forma em vez de um literal por vista.
 *
 * A altura vem por argumento porque nem todas as cadeiras têm a mesma: 36 é o
 * fallback de um assento `16×18 · 2` (cozinha e meeting-room), a de escritório é
 * `16×20 · 2` = 40. O valor TEM de ser o dobro do que `PROP_DRAWERS` declara para
 * o mesmo `PropKind`, senão a cadeira muda de tamanho consoante o PNG carregue —
 * e há um teste a confrontar as duas medidas por essa exacta razão.
 */
const cadeira = (alvo: string, fonte: string, alt = 36): Peca => ({
  alvo,
  fonte,
  corte: { x: 0, y: 0, w: 32, h: alt },
  w: 32,
  h: alt,
  raio: 0,
  espelho: false,
});

/**
 * Uma casa de exposição do pacote `casas_selecionadas`: ficheiro inteiro, cópia
 * pixel a pixel, sem espelho e sem raio.
 *
 * Dez peças que só diferem em `alvo`, `fonte` e medida não valem dez literais de
 * sete linhas — valem esta função e dez chamadas de uma linha, pela mesma razão
 * que as cadeiras acima têm a sua.
 *
 * As medidas TÊM de ser o dobro do que `PROP_DRAWERS` declara para o mesmo
 * `PropKind` (o canvas do `texture()` é sempre 2× as dimensões da arte), senão a
 * casa mudava de tamanho consoante o PNG carregasse ou não — e há um teste a
 * confrontar os dois lados exactamente por isso.
 */
const casaExpo = (alvo: string, fonte: string, w: number, h: number): Peca => ({
  alvo,
  fonte,
  corte: { x: 0, y: 0, w, h },
  w,
  h,
  raio: 0,
  espelho: false,
});

/**
 * Exportada para o teste, e só para isso: a lista é um contrato com `space.ts`
 * (pegada × TILE) que se parte em silêncio se alguém mexer num lado só.
 */
export const PECAS: readonly Peca[] = [
  {
    // Mesa de reunião: pegada 3×2 tiles = 96×64, arte 96×72 (fallback de
    // `meetingTable` = 48×36 · 2). O ficheiro `meeting-table-v1` já vem no tamanho
    // final e SEM as cadeiras à volta — foi tratado em offline a partir da
    // meeting-room nova — por isso o corte é o ficheiro inteiro,
    // `escalaNitida(96,72,96,72)` dá k=1 e o recorte é uma CÓPIA pixel a pixel. Se
    // o PNG faltar, fica o `drawMeetingTable` do v1, do MESMO tamanho: o 404 não
    // encolhe a mesa (que era a armadilha das versões que reduziam no browser).
    //
    // 72 de altura para 64 de pegada é DE PROPÓSITO: os 8 px que sobram
    // transbordam para CIMA (a arte cola-se com `setOrigin(0.5, 1)`) e é isso que
    // encosta as cadeiras do lado norte ao tampo, em vez de as deixar a um palmo.
    //
    // Sem raio: os cantos já vêm arredondados no ficheiro, não há esquinas a mascarar.
    alvo: propKey("meetingTable"),
    fonte: MESA,
    corte: { x: 0, y: 0, w: 96, h: 72 },
    w: 96,
    h: 72,
    raio: 0,
    espelho: false,
  },
  {
    // Secretárias dos FUNCIONÁRIOS da TI: pegada 3×1 tiles = 96×32, arte 90×52
    // (fallback de `deskDual` = 45×26 · 2). São as quatro `deskDual` das rows 4 e 7
    // (`space.ts`), cada uma com dois postos. O ficheiro `ti-desk-v1` já vem no
    // tamanho final, sem cadeira colada por baixo, por isso o corte é o ficheiro
    // inteiro (k=1, cópia pixel a pixel) e o 404 não muda o tamanho da peça.
    //
    // ATENÇÃO ao ler estes números: 90×52 é a TELA, não o desenho. A bancada
    // ocupa 82×52 centrados (4 px transparentes de cada lado) — foi assim que
    // sempre esteve (era 76×48 numa tela de 90) e é o que permite crescer sem
    // reescrever a largura do plano B. A proporção que interessa é a do DESENHO:
    // 82/52 = 1,577 contra os 1,5816 da caixa opaca do render de origem
    // (`sala-de-ti/setup-do-ti.png`, 1425×901) — 0,3 % de desvio. Quem mexer nisto
    // mede a caixa de alfa do PNG, não a tela; a tela mente por 4 px de cada lado.
    //
    // O pedido foi «aumente um poouquinho … bem poucio»: 76×48 → 82×52, +8 % em
    // cada eixo. O passo seguinte da escada (90×56, encher a tela toda) era +18 %
    // e já não é «bem pouco». A tela FICA a 90 de propósito: 90 ≤ 96 da pegada,
    // logo as três bancadas contíguas da row 4 continuam sem transbordo LATERAL.
    //
    // Os 52 de altura transbordam 20 px para cima da pegada de 32: os monitores
    // sobem por cima da cadeira da fila de trás — a secretária fica À FRENTE dela.
    alvo: propKey("deskDual"),
    fonte: CONSOLA,
    corte: { x: 0, y: 0, w: 90, h: 52 },
    w: 90,
    h: 52,
    raio: 0,
    espelho: false,
  },
  {
    // Secretária de Vendas: pegada 2×1 tiles = 64×32.
    //
    // Substitui o desenho de código nos postos da sala de Vendas — e só nesses. A
    // secretária do SUPERVISOR da TI é uma `deskPC`, kind à parte de propósito: é
    // o que permite dar arte DIFERENTE às duas salas (Vendas ≠ supervisor, cada
    // uma com o seu ficheiro). Sem o kind separado, `prop:deskPC` trocaria a arte
    // das duas de uma vez só.
    //
    // O corte é o ficheiro inteiro porque ele já vem cortado: 64×46, sem margem
    // transparente, preparado em offline a partir de um render de 1448×1086. Sem
    // redução a fazer aqui, `escalaNitida(64, 46, 64, 46)` dá k=1 e o recorte é
    // uma CÓPIA — nenhum pixel é reamostrado no browser.
    //
    // Já esteve a 96×70 sobre 3 tiles. Ao pixel a arte era melhor; em jogo era a
    // maior peça do escritório e passava a cabeça do avatar de 32×48. O ficheiro
    // foi reduzido OUTRA VEZ a partir do render original, não a partir do PNG de
    // 96 — reduzir o já reduzido é somar duas perdas de resolução por nada.
    //
    // 46 de altura para 32 de pegada: os 14 px a mais sobem, e são eles que põem
    // o monitor acima do tampo em vez de o encaixarem no chão. É o mesmo negócio
    // da consola ao lado, quase com a mesma folga.
    alvo: propKey("salesDesk"),
    fonte: SECRETARIA,
    corte: { x: 0, y: 0, w: 64, h: 46 },
    w: 64,
    h: 46,
    raio: 0,
    espelho: false,
  },
  {
    // Balcão da RECEPÇÃO: pegada 3×1 tiles = 96×32, arte 96×52.
    //
    // A pegada ENCOLHEU de 4 para 3 tiles com esta troca, e é a parte da mudança
    // que não se vê no ficheiro. 96 px de arte em 96 px de pegada encaixa ao
    // pixel; a 4 tiles sobravam 32 px de colisão vazia de um dos lados, que é
    // exactamente a parede invisível de que já houve queixa nas mobile homes.
    //
    // Encolher também alinhou a cadeira: com 3 tiles o balcão ocupa as cols 16-18
    // e o meio dele cai em 560, que é o centro exacto da col 17 onde a cadeira
    // está. Desvio ZERO, sem depender do empurrão de assento para nada.
    //
    // Os 20 px que a arte tem a mais do que a pegada sobem — são o monitor e a
    // planta pousados na bancada, que é o que faz o balcão ler como balcão.
    alvo: propKey("receptionDesk"),
    fonte: RECEPCAO,
    corte: { x: 0, y: 0, w: 96, h: 52 },
    w: 96,
    h: 52,
    raio: 0,
    espelho: false,
  },
  {
    // Bebedouro: pegada 1×1 tile = 32×32, arte 16×44.
    //
    // A ÚNICA peça do ficheiro mais ESTREITA do que a própria pegada, e de
    // propósito: um bebedouro de garrafão tem 30-35 cm de frente e o tile tem um
    // metro. Encher os 32 px dava um armário, não um bebedouro. A colisão continua
    // a ser o tile inteiro — pisar o meio metro de ar ao lado de um electrodoméstico
    // não é caminho que faça falta a ninguém, e é uma linha de `space.ts` a menos.
    //
    // Cresceu de 14×38 a pedido («aumente o tamanho dos bebedouros»), e o tamanho
    // não se escolheu a olho: `texture()` desenha sempre a 2×, por isso os dois
    // lados da arte são obrigatoriamente PARES e a escada de tamanhos é grossa.
    // Contra a caixa opaca do render (484×1298 → 0,3729) só há quatro degraus:
    //
    //   14×38  −1,2 %   o que estava
    //   16×42  +2,2 %   cresce menos
    //   16×44  −2,5 %   ← este
    //   18×48  −0,6 %   melhor proporção, REJEITADO: 48 é a altura do avatar
    //
    // O 18×48 seria o mais fiel e é precisamente o que a régua desta sala proíbe —
    // «a régua é o boneco», e um bebedouro que dá pela cabeça de quem lá bebe lê-se
    // como cenário. Aos 27,4 px/m que os 48 px do avatar fixam, 44 px = 1,60 m: o
    // aparelho com o garrafão em cima, que é o que a coisa mede na vida real.
    // Antes de tudo isto media 32×52 — 1,9 m, mais alto do que uma pessoa.
    //
    // 16 de largura é EXACTAMENTE meio tile. É o último degrau que ainda deixa a
    // peça ler como estreita; 18 já enchia mais de metade da pegada.
    alvo: propKey("waterCooler"),
    fonte: BEBEDOURO,
    corte: { x: 0, y: 0, w: 16, h: 44 },
    w: 16,
    h: 44,
    raio: 0,
    espelho: false,
  },
  {
    // Estante: pegada 2×1 tiles, arte 64×54 (era 64×56 só de desenho).
    //
    // A largura NÃO é negociável — 64 é a pegada, e uma estante que não a enche
    // deixa fresta contra a parede em três salas ao mesmo tempo. Fixados os 64, a
    // altura sai da caixa limpa do render (899×747, rácio 1,2035) e só pode ser
    // par: 54 dá 1,1852, ou seja −1,52 % de esticão vertical. A alternativa de
    // rácio melhor era 60×50 (−0,29 %) e perdia por deixar 4 px de pegada vazia —
    // 1,5 % não se vê, uma fresta ao lado da parede vê-se.
    //
    // 54 px = 1,97 m aos 27,4 px/m que o avatar fixa: uma estante de quatro
    // prateleiras que dá pouco acima da cabeça de quem lá vai buscar um livro.
    alvo: propKey("bookshelf"),
    fonte: PRATELEIRA,
    corte: { x: 0, y: 0, w: 64, h: 54 },
    w: 64,
    h: 54,
    raio: 0,
    espelho: false,
  },
  {
    // Sofá: pegada 3×1 tiles, arte 96×46 (era 96×48 de desenho).
    //
    // Mesma lógica da estante ao contrário: aqui a largura da pegada (96) e o
    // rácio do render (924×445 = 2,0764) quase coincidem — 96×46 fica a +0,51 %,
    // o melhor desvio de todas as peças deste ficheiro. Encolheu 2 px em altura
    // por causa disso, não por gosto.
    //
    // ATENÇÃO ao mexer nesta altura: o sofá é um ASSENTO. O `CUSHION_TOP.couch`
    // em `officeArt.ts` conta os pixels DO FUNDO até ao topo da almofada, e nesta
    // arte a almofada do assento começa em y=20 de 46 → 26 (era 23 numa arte de
    // 48). Medido no perfil de luminância do próprio PNG, não a olho: a costura
    // escura entre o encosto e o assento está em y=19 e a almofada clara ocupa
    // y=20..28. Trocar o PNG sem trocar esse número põe o boneco a flutuar.
    alvo: propKey("couch"),
    fonte: SOFA,
    corte: { x: 0, y: 0, w: 96, h: 46 },
    w: 96,
    h: 46,
    raio: 0,
    espelho: false,
  },
  // ── O sofá de PERFIL da sala de TI, em três fatias de 40×60 ────────────────
  //
  // Único móvel do ficheiro cujo alvo é uma chave de FATIA e não de prop. As
  // três montam um composto de 34×124: a fatia i assenta na base da sua linha de
  // chão, logo o fundo de cada uma fica 32 px abaixo do fundo da anterior, e um
  // composto de N fatias mede 32·(N−1) + h.
  //
  // A ALTURA não é uma escolha: a pegada de 3 tiles fixa o passo das almofadas em
  // 32 px, portanto o estofo mede 96 e, com os dois braços, o composto dá 124. É
  // por isso que «diminuir o sofá» não pode passar por encolher o composto — a
  // 112, por exemplo, as almofadas passavam a 28,9 contra um passo de 32 que os
  // tiles impõem, e o terceiro sentado ficava 6 px fora da sua, em cima da
  // costura. A altura está presa; a largura é a única folga.
  //
  // A LARGURA seguiu o render até 2026-09-01: caixa opaca de 429×1330 (limiar de
  // alfa estável dos 8 aos 250), rácio 3,100, logo 124/3,100 = 40. A 40 o móvel
  // entrava 4 px pela parede de cada lado do tile de 32 e lia-se maior do que a
  // sala de TI, que só tem esta coluna livre. A pedido, 40 → 34: transborda 1 px
  // por lado e o sofá deixa de dominar a sala. Abaixo disto o encosto achata —
  // a 32 as almofadas já perdem o volume e o móvel lê-se como um banco corrido.
  //
  // 429 → 40 foi 0,093× em offline, com filtro de caixa em alfa pré-multiplicado.
  // O 40 → 34 é uma SEGUNDA passagem, feita também em offline (lanczos3, eixo X
  // só) porque o render de origem está num .rar que esta máquina não abre; a 0,85×
  // num eixo só, sobre arte já suavizada, não se vê. O que continua a não
  // acontecer é reamostrar no BROWSER: aqui o recorte é uma CÓPIA (k = 1,
  // `escalaNitida` a devolver `true`), tal como na secretária de Vendas e nas casas.
  //
  // Cada fatia carrega só a SUA banda do composto e é transparente no resto:
  //   0 → composto 0..45   (local 0..45)   braço norte + almofada 1
  //   1 → composto 45..77  (local 13..45)  almofada 2
  //   2 → composto 77..124 (local 13..60)  almofada 3 + braço sul + base
  // As fronteiras caem nas COSTURAS das almofadas (origem y 575 e 920), não em
  // marcas de 32 px: com marcas de 32, a fatia de baixo pintava o terço inferior
  // da almofada da de cima — e, como leva mais profundidade, pintava-o À FRENTE
  // de quem lá estivesse sentado. Nenhuma linha do composto é pintada duas vezes,
  // o que também evita somar duas vezes o alfa das bordas suavizadas.
  //
  // Ao trocar estes PNG mexer TAMBÉM em `PROP_DRAWERS.couchSide` (17×30 · 2 =
  // 34×60): há um teste a confrontar as duas medidas, para o sofá não mudar de
  // tamanho consoante os ficheiros carreguem ou dêem 404. E `CUSHION_TOP.couchSide`
  // vale 26 sobre uma fatia de 60 — o ponto de sentar cai a dois terços da
  // almofada nas três, que é o que faz os três lugares assentarem igual.
  {
    alvo: propSliceKey("couchSide", 0),
    fonte: SOFA_LAT_0,
    corte: { x: 0, y: 0, w: 34, h: 60 },
    w: 34,
    h: 60,
    raio: 0,
    espelho: false,
  },
  {
    alvo: propSliceKey("couchSide", 1),
    fonte: SOFA_LAT_1,
    corte: { x: 0, y: 0, w: 34, h: 60 },
    w: 34,
    h: 60,
    raio: 0,
    espelho: false,
  },
  {
    alvo: propSliceKey("couchSide", 2),
    fonte: SOFA_LAT_2,
    corte: { x: 0, y: 0, w: 34, h: 60 },
    w: 34,
    h: 60,
    raio: 0,
    espelho: false,
  },
  {
    // Televisão da Sala de Descanso: pegada 3×1 tiles, arte 84×54.
    //
    // Encheu a pegada ao pixel (96×62) até 2026-08-31, e a queixa foi «diminua um
    // pouco o tamanho da tv». Encher a pegada era uma regra que eu próprio tinha
    // escrito para justificar os 96, não uma exigência do mundo: a peça não é
    // sólida e não há chão pintado por baixo, portanto uma arte mais estreita não
    // abre fresta nenhuma — sobra relva/parede dos dois lados, 6 px de cada, que
    // é o que uma televisão encostada a uma parede faz.
    //
    // O que MANDA continua a ser o rácio da caixa opaca do render (687×441 =
    // 1,5578). 84×54 dá 1,5556: −0,14 % de esticão, melhor do que os −0,60 % que
    // os 96×62 tinham. Ambos PARES, porque o plano B em código sai sempre ao
    // dobro da grelha e uma medida ímpar era impossível de igualar.
    //
    // Os 22 px que passam dos 32 da pegada transbordam para CIMA (a arte cola-se
    // com `setOrigin(0.5, 1)`), que é o lado permitido e o certo: a TV está
    // encostada à parede sul e é o painel que sobe pela parede, não o pé que
    // avança para o chão de quem passa. O `PROP_SPECS.tv` continua `solid: false`
    // — está pendurada, ninguém esbarra nela.
    //
    // Ao trocar este PNG mexer TAMBÉM em `PROP_DRAWERS.tv` (42×27 · 2): há um
    // teste a confrontar as duas medidas, para a televisão não mudar de tamanho
    // consoante o ficheiro carregue ou dê 404.
    alvo: propKey("tv"),
    fonte: TV,
    corte: { x: 0, y: 0, w: 84, h: 54 },
    w: 84,
    h: 54,
    raio: 0,
    espelho: false,
  },
  {
    // Mesa da cozinha/copa: pegada 2×2 tiles = 64×64, arte 64×56 (fallback de
    // `diningTable` = 32×28 · 2). Ficheiro já no tamanho final → corte inteiro,
    // k=1, cópia pixel a pixel; se faltar, fica o `drawDiningTable` do v1 sem
    // mudar de tamanho. Os 8 px a menos que a pegada deixam o tampo assente no
    // chão do tile, como uma mesa baixa de copa se vê de cima.
    alvo: propKey("diningTable"),
    fonte: COZ_MESA,
    corte: { x: 0, y: 0, w: 64, h: 56 },
    w: 64,
    h: 56,
    raio: 0,
    espelho: false,
  },
  {
    // Secretária do SUPERVISOR da TI: pegada 3×1 tiles = 96×32, arte 96×54
    // (fallback de `deskPC` = 48×27 · 2). É a ÚNICA `deskPC` do mapa (`space.ts`:
    // col 30, row 4) — as dos funcionários são `deskDual` e a de Vendas é
    // `salesDesk`, por isso tomar `prop:deskPC` só troca a arte do supervisor e
    // deixa as outras duas salas intactas. Os 22 px a mais sobem: os monitores
    // ficam acima do tampo em vez de encaixados no chão.
    //
    // Andou 2 tiles (64×56) até 2026-08-26, e a queixa foi "aumente o tamanho da
    // mesa do supervisor". Não era só escala: 64×56 dá a proporção 1,143 e a
    // caixa opaca do render tem 1,7985 — a mesa estava ESPREMIDA na horizontal,
    // e a cadeira de escritório nova (32×40) debaixo dela tornou isso óbvio ao
    // ocupar metade da largura do tampo. Pior: o supervisor tinha a MENOR
    // secretária da sala, 64 px contra os 90 das consolas dos funcionários. A
    // 96×54 a proporção é a do render (desvio de -1,15%) e a largura ÍMPAR ainda
    // põe a cadeira na col do meio (`space.ts` explica o encaixe a zero).
    alvo: propKey("deskPC"),
    fonte: TI_SUP,
    corte: { x: 0, y: 0, w: 96, h: 54 },
    w: 96,
    h: 54,
    raio: 0,
    espelho: false,
  },
  {
    // Quadro branco da meeting-room: pegada 4×1 tiles = 128×32, arte 128×48
    // (fallback de `whiteboard` = 64×24 · 2). Não é sentável nem sólido — encosta
    // à parede norte da sala (`space.ts`: col 16, row 4). Os 16 px a mais sobem
    // pela parede, como um quadro pendurado sobe acima da linha da secretária.
    alvo: propKey("whiteboard"),
    fonte: MTG_QUADRO,
    corte: { x: 0, y: 0, w: 128, h: 48 },
    w: 128,
    h: 48,
    raio: 0,
    espelho: false,
  },
  // Cadeiras da cozinha (`diningChair`) e da meeting-room (`meetingChair`). Cada
  // modelo entra sob as chaves de assento que o mundo desenha: só a `base` das
  // três vistas frontais/laterais (a camada `front` dessas fica vazia no código,
  // e continua vazia), e `base`+`front` da vista de costas — o encosto passa À
  // FRENTE de quem se senta, por isso vem num ficheiro à parte que o mundo
  // desenha por cima do avatar. A vista `right` traz arte própria (não é a `left`
  // espelhada como no fallback): o pacote deu uma cadeira direita a sério.
  cadeira(seatPropKey("diningChair", "down", "base"), COZ_CAD_F),
  cadeira(seatPropKey("diningChair", "left", "base"), COZ_CAD_E),
  cadeira(seatPropKey("diningChair", "right", "base"), COZ_CAD_D),
  cadeira(seatPropKey("diningChair", "up", "base"), COZ_CAD_CB),
  cadeira(seatPropKey("diningChair", "up", "front"), COZ_CAD_CF),
  cadeira(seatPropKey("meetingChair", "down", "base"), MTG_CAD_D),
  cadeira(seatPropKey("meetingChair", "left", "base"), MTG_CAD_E),
  cadeira(seatPropKey("meetingChair", "right", "base"), MTG_CAD_R),
  cadeira(seatPropKey("meetingChair", "up", "base"), MTG_CAD_CB),
  cadeira(seatPropKey("meetingChair", "up", "front"), MTG_CAD_CF),
  // A cadeira de escritório dos postos de TI e de Vendas — 32×40, mais alta do
  // que as outras duas porque o encosto de malha sobe mais do que o de uma
  // cadeira de cozinha. Só DUAS chaves, e é honesto: contadas no mapa, as nove
  // `officeChair` sentam-se TODAS viradas a norte, portanto a vista de costas é
  // a única que o escritório alguma vez desenha; as outras seis chaves ficam com
  // o desenho em código, que continua a servir de rede sem nunca aparecer.
  // O corte em y=24 (de 40) é a linha mais estreita da peça — o pescoço entre o
  // assento e a coluna de gás — e não come cabeças: com o avatar sentado, o topo
  // dele fica 29 px ACIMA do topo da cadeira, por isso a camada da frente só
  // pode tapar os 19 px de baixo do corpo.
  cadeira(seatPropKey("officeChair", "up", "base"), CAD_ESC_B, 40),
  cadeira(seatPropKey("officeChair", "up", "front"), CAD_ESC_F, 40),
  {
    // Bastidor do bunker: pegada 1×1 tile = 32×32.
    //
    // O recorte começa em y=52 e não em y=0 porque o ficheiro traz um halo de
    // luz azul à volta da peça — a caixa de alfa apanhava-o e o bastidor vinha
    // com um nimbo rectangular à volta. 52 é onde a CHAPA começa.
    //
    // Os 83 de altura foram medidos a olhar para a arte, não deduzidos da caixa:
    // o corpo do bastidor acaba por volta da linha 126 e depois há um RODAPÉ —
    // a base em que ele assenta — até à 133. O recorte «bonito» de 76 px cortava
    // exactamente esses pés e o bastidor ficava a flutuar.
    //
    // 32×70 e não 32×64: o par tem de ser PAR nos dois eixos. Um desenho de
    // recurso sai sempre de `texture(w, h, …)` a 2× em ambos os lados, logo tem
    // altura par à força; 32×69 (a proporção exacta) era impossível de igualar e
    // a arte do pacote e o desenho de código ficavam com tamanhos diferentes —
    // o mesmo bastidor mudava de tamanho consoante o PNG tivesse carregado. 70
    // afasta-se 0,15% da proporção do original: invisível.
    //
    // A altura transborda 38 px acima da pegada de uma linha. É isso que faz o
    // bastidor parecer alto de pé encostado à parede, em vez de um cubo no chão.
    alvo: propKey("serverRack"),
    fonte: BASTIDOR,
    corte: { x: 29, y: 52, w: 38, h: 83 },
    w: 32,
    h: 70,
    raio: 0,
    espelho: false,
  },
  {
    // A máquina do meio: pegada 5×2 tiles = 160×64.
    //
    // Duas torres laterais com ecrãs, uma parede de ecrã ao centro e a bancada
    // com dois teclados. É a peça que o Matheus apontou («essa maquina do meio
    // ai») e é por causa dela que a sala de automações tem largura ÍMPAR: uma
    // pegada de 5 tiles centra-se num CENTRO de tile, e só uma sala ímpar tem um
    // centro de tile ao meio. Numa sala par a máquina ficava meio tile ao lado.
    //
    // O recorte corta 9 px em cima e para em y=159: as linhas 159-167 do
    // original são só a sombra difusa projectada no chão, que aqui não serve
    // (o mundo já tem a sua própria sombra por baixo dos props). Cortá-las é o
    // que torna a redução EXACTA — 250×150 para 160×96 é 0,64× nos dois eixos,
    // ao pixel, e nada estica num lado mais do que no outro.
    //
    // 96 de altura para 64 de pegada: os 32 px de sobra sobem, e é o que põe a
    // parede de ecrã acima da linha dos olhos de quem passa à frente.
    alvo: propKey("aiConsole"),
    fonte: MAQUINA,
    corte: { x: 19, y: 9, w: 250, h: 150 },
    w: 160,
    h: 96,
    raio: 0,
    espelho: false,
  },
  {
    // Ecrã de parede: pegada 3×1 tiles = 96×32.
    //
    // Painel com moldura e gráficos azuis e verdes. Fica na linha de cima da
    // sala, encostado à parede norte, e existe para a sala não ser só bastidores.
    //
    // A largura do recorte é 112 e a caixa de alfa dá 111. O pixel a mais é uma
    // coluna TRANSPARENTE da margem direita, e é de propósito: 112×56 é 2:1
    // exacto, logo reduz a 96×48 com o mesmo factor (6/7) nos dois eixos. Com
    // 111 a moldura ficava um pixel mais estreita de um lado do que do outro.
    //
    // Os 48 de altura transbordam 16 px acima da pegada de uma linha — o painel
    // sobe pela parede, como um painel de parede sobe.
    alvo: propKey("analyticsScreen"),
    fonte: PAINEL,
    corte: { x: 38, y: 23, w: 112, h: 56 },
    w: 96,
    h: 48,
    raio: 0,
    espelho: false,
  },
  {
    // Mobile home do stand: pegada 7×4 tiles = 224×128.
    //
    // É a peça que trouxe este módulo para a rua. O parque tinha três desenhos
    // em código — fachada comprida beige, a mesma com saia vermelha, e uma
    // empena a recuar — e nenhum deles é o que a arte do pacote traz: uma casa
    // vista em 3/4, com telhado escuro, porta ao meio da fachada, degraus de
    // madeira e arbustos floridos aos lados. Trocar a arte sem trocar as pegadas
    // deixava a colisão a descrever caravanas que já não existem, por isso os
    // três `PropKind` deram lugar a dois (`mobileHome` e o seu espelho).
    //
    // O recorte é a caixa de alfa INTEIRA (medida no ficheiro: x 9→303, y 7→280,
    // logo 295×274) e não um rectângulo escolhido a olho: aqui não há nada para
    // deixar de fora. Ao contrário da mesa de reunião, esta casa não vem com
    // cadeiras que tenham de virar props — os arbustos são cenário e é assim que
    // ficam bem, colados à casa.
    //
    // 160×148 sai de duas contas. 160 é a pegada (5 × 32) ao pixel — a arte
    // NUNCA pode ser mais larga do que a pegada, ou a casa cobre chão onde se
    // anda. 148 mantém a proporção do recorte com 0,41% de desvio (160·274 =
    // 43840 contra 148·295 = 43660) e é PAR, que é obrigatório: o desenho de
    // recurso sai de `texture(w, h, …)` a 2× em ambos os eixos, logo 149 seria
    // impossível de igualar e a casa mudava de tamanho consoante o PNG tivesse
    // carregado ou não.
    //
    // Encolheu DUAS vezes, por medição e não por opinião. Era 224×208 sobre 7×4:
    // a PAREDE (do beiral ao chão, em pixels de ecrã) dava 76 px contra os 67 da
    // `Arch`, e a silhueta 201 px contra 160, na MESMA projecção de 3/4 — 26%
    // mais alta. A 178 a parede ficou em 65 px e a silhueta em 172, dentro do
    // lote mas ainda a maior das duas casas em 3/4; foi por isso que continuou a
    // ler-se grande. A 148 a parede dá 54 px e a silhueta 143, contra 67 e 160 da
    // `Arch` — passa a ser a mais PEQUENA do parque, que é o pedido.
    //
    // O comentário que aqui estava dizia o contrário — que as três tinham
    // crescido pelo mesmo ≈1,4× e que a altura da parede era igual nas três.
    // Nenhuma das duas coisas resistiu à medição; era o que mantinha o erro.
    //
    // Os 20 px que a altura tem a mais do que a pegada sobem ACIMA dela (a arte
    // cola-se com `setOrigin(0.5, 1)`): é o telhado a passar por cima da cabeça
    // de quem anda encostado à casa, que é o que um beiral faz. Eram 50 px; ao
    // encolher, o beiral encolheu com ele e a casa tapa menos quem lá passa.
    //
    // 160/295 = 0,542× — continua a ser REDUÇÃO e continua acima do piso de 0,5
    // abaixo do qual o `drawImage` directo do `recortar` salta linhas. É a margem
    // que resta: mais pequena do que isto exige reamostragem em dois passos.
    alvo: propKey("mobileHome"),
    fonte: CASA,
    corte: { x: 9, y: 7, w: 295, h: 274 },
    w: 160,
    h: 148,
    raio: 0,
    espelho: false,
  },
  {
    // A MESMA casa ao espelho. Um segundo ficheiro para isto seria arte a mais
    // para nada: espelhar é de graça e a leitura em 3/4 tem exactamente duas
    // orientações honestas. Hoje já não é este par que enche o stand — desde que
    // entraram a `Side` e a `Arch`, só a versão original é que fica, uma vez.
    alvo: propKey("mobileHomeFlip"),
    fonte: CASA,
    corte: { x: 9, y: 7, w: 295, h: 274 },
    w: 160,
    h: 148,
    raio: 0,
    espelho: true,
  },
  {
    // Mobile home em alçado lateral: pegada 9×3 tiles = 288×96, arte 288×100.
    //
    // Vem do render que o dono entregou em 1536×1024 com fundo de papel e sombra
    // projectada por baixo. Foi tratado fora do repo antes de entrar: fundo
    // removido por alagamento a partir da moldura, sombra removida por um SEGUNDO
    // alagamento restrito a pixels neutros e claros (a casa é saturada ou escura,
    // logo o alagamento morre na silhueta), e só depois reduzido. A sombra tinha
    // de sair porque o mundo já desenha a sua — a elipse de contacto de
    // `spec.w * TILE * 0,84` — e duas sombras sob a mesma casa é um borrão.
    //
    // Ficou com a caixa de alfa em 1167×402 (2,9030). 288 é a pegada ao pixel e
    // 288/2,9030 = 99,2 → 100, PAR e a 0,79% da proporção real. Foi por isso que
    // a pegada é de 9 tiles e não de 8 ou 10: com 8 dava 88 px de alto, faixa de
    // parede de 46,5 px, e a casa ficava mais baixa do que as duas vizinhas em
    // 3/4; com 10 dava 110 e 58,2 px de parede, um palácio ao lado delas. A 100,
    // a faixa mede 52,9 px contra os 50,2 px da `v1` e os 51,8 px da de arco —
    // 5,4% entre a mais baixa e a mais alta — e é a ALTURA DA PAREDE, do beiral
    // ao chão, a única medida que significa a mesma coisa nas três projecções,
    // que faz as três lerem como casas do mesmo lote em vez de três maquetas de
    // escalas diferentes.
    //
    // O ficheiro foi REGERADO a 288×100 (não esticado aqui) precisamente para o
    // corte poder continuar a ser o ficheiro inteiro à escala 1: `escalaNitida`
    // devolve true, o `recortar` desliga a suavização e o browser copia pixel a
    // pixel. Ampliar 192→288 no browser dava um borrão; ampliar em offline, a
    // partir do render de 1536 px que ainda tem a resolução toda, não dá.
    alvo: propKey("mobileHomeSide"),
    fonte: CASA_LADO,
    corte: { x: 0, y: 0, w: 288, h: 100 },
    w: 288,
    h: 100,
    raio: 0,
    espelho: false,
  },
  {
    // O alçado lateral ao espelho. É o ÚNICO modelo que aparece duas vezes no
    // parque, e aparece uma vez de cada lado desta linha: espelhado em cima à
    // direita, direito em baixo à esquerda. Repetir na diagonal e com a fachada
    // trocada é o que impede que se leiam como um par.
    alvo: propKey("mobileHomeSideFlip"),
    fonte: CASA_LADO,
    corte: { x: 0, y: 0, w: 288, h: 100 },
    w: 288,
    h: 100,
    raio: 0,
    espelho: true,
  },
  {
    // Mobile home de telhado em arco: pegada 7×4 tiles = 224×128, arte 224×160.
    //
    // Mesmo tratamento do alçado lateral (fundo e sombra fora, redução com alfa
    // pré-multiplicado para não deixar halo branco nas arestas). Caixa de alfa
    // em 920×661 = 1,3918; 224/1,3918 = 160,9 → 160, PAR, 0,59% de desvio. Tal
    // como o alçado, o ficheiro foi REGERADO neste tamanho a partir do render
    // original — nada é ampliado no browser, e o corte volta a ser escala 1.
    //
    // Esta é a peça de REFERÊNCIA da escala do parque, e ficou intacta quando a
    // `v1` encolheu: 7×4 e 224×160. Foi contra os seus 67 px de parede que a
    // outra se mediu — e é ela que está certa, porque tem 32 px de arte acima da
    // pegada onde a `v1` chegava a ter 80: menos plano de telhado à vista e mais
    // fachada, que é a leitura correcta a esta distância.
    alvo: propKey("mobileHomeArch"),
    fonte: CASA_ARCO,
    corte: { x: 0, y: 0, w: 224, h: 160 },
    w: 224,
    h: 160,
    raio: 0,
    espelho: false,
  },
  {
    // A de arco ao espelho — é esta que fica no canto inferior direito, a fechar
    // a diagonal com a `v1` que ficou em cima à esquerda. As duas casas em 3/4
    // ficam assim numa diagonal e os dois alçados na outra.
    alvo: propKey("mobileHomeArchFlip"),
    fonte: CASA_ARCO,
    corte: { x: 0, y: 0, w: 224, h: 160 },
    w: 224,
    h: 160,
    raio: 0,
    espelho: true,
  },
  // ── As quatro casas novas. A LARGURA de cada uma é a pegada declarada em
  // `PROP_SPECS` vezes 32, ao pixel — 5 tiles dão 160, 6 dão 192 — e não por acaso:
  // foi a largura que se escolheu ao assar, e a altura saiu da proporção medida da
  // caixa opaca do render, arredondada ao par acima (o `texture()` só produz
  // medidas pares, porque duplica sempre). Por isso nenhuma destas entra em
  // `PARK_HOME_PROPS`: a normalização por largura que lá está não teria nada para
  // corrigir.
  //
  // As alturas passam da profundidade da pegada (96 px para 3 linhas) ou igualam-na
  // e isso é o que se quer: o excedente sobe, porque a arte se cola pelo pé
  // (`setOrigin(0.5, 1)`), e telhado a mais é telhado a assomar por cima de quem
  // passa atrás — de graça. Largura a mais é que seria veneno: pintava relva
  // andável e ficava uma parede invisível. Daí a largura ser exacta e a altura
  // livre — o modular chega aos 116 e ninguém dá por bloqueio nenhum, porque os 20
  // px que passam ficam ACIMA da pegada.
  //
  // «Livre» não é «de graça», e a cúpula foi a prova: com 152 px passava 56 acima
  // da pegada, quase o triplo da vizinha mais folgada, e com a MENOR área de chão
  // das quatro. Uma casa que sobe muito e assenta pouco lê-se a voar — foi o que
  // se ouviu. Desceu a 128×122 (a largura da nova pegada de 4, a altura pela
  // proporção medida), e os 56 px de folga passaram a 26.
  casaExpo(propKey("homeDome"), CASA_DOMO, 128, 122),
  casaExpo(propKey("homeCapsuleClosed"), CASA_CAPS_F, 160, 96),
  casaExpo(propKey("homeModular"), CASA_MODULAR, 192, 116),
  casaExpo(propKey("homeCapsule"), CASA_CAPS, 224, 116),
  {
    // O LAGO. A única peça deste ficheiro cujo alvo não é um móvel: é uma chave de
    // MUNDO (`world:lake`), desenhada pelo chão e não pela lista de props.
    //
    // 448×224 sai da elipse declarada em `space.ts` — `rx: 7`, `ry: 3,5` a partir
    // de (16,5 · 29,5) dá as colunas 9,5-23,5 e as linhas 26-33, ou seja 14×7
    // tiles. Os raios são meios-tiles EXACTOS por causa desta linha: a moldura cai
    // em pixels inteiros, a arte é reduzida uma única vez em offline para esse
    // tamanho, e aqui o corte é o ficheiro inteiro — escala 1, sem reamostragem.
    //
    // O render veio a 1260×830 (1,5181) e a redução para 2:1 achata-o em 0,759.
    // Não é distorção: o mundo vê-se de cima em escorço e um lago redondo visto a
    // prumo TEM de comprimir na vertical para assentar no chão. A alternativa —
    // manter 1,5181 — punha 295 px de água onde a colisão só veda 224, e a água
    // passava a entrar pela relva pisável e pela cerca.
    alvo: LAKE_ART,
    fonte: LAGO,
    corte: { x: 0, y: 0, w: 448, h: 224 },
    w: 448,
    h: 224,
    raio: 0,
    espelho: false,
  },
  {
    // A CASA FLUTUANTE, em cima da água: pegada 6×3 tiles = 192×96, arte 192×98.
    //
    // Mesmo tratamento das duas mobile homes preparadas ao pixel — fundo removido
    // por alagamento a partir da moldura, ilhas soltas descartadas, redução com
    // alfa pré-multiplicado. Caixa de alfa em 1068×549 = 1,9454; 192/1,9454 =
    // 98,69 → 98, PAR (a arte de plano B desenha a 2×) e a 0,7% da proporção real.
    //
    // Sobram 2 px acima da pegada e isso é a peça toda a dizer o que é: uma casa
    // baixa vista de muito raso, em que o telhado quase não se levanta do chão que
    // ocupa. Uma mobile home em 3/4 sobe 18 ou 52 px acima da sua; esta sobe 2.
    alvo: propKey("floatingHouse"),
    fonte: CASA_LAGO,
    corte: { x: 0, y: 0, w: 192, h: 98 },
    w: 192,
    h: 98,
    raio: 0,
    espelho: false,
  },
  {
    // CARTAZ à entrada do parque: pegada 3×1 tiles = 96×32, arte 96×106.
    //
    // A peça com MAIS transbordo vertical do ficheiro todo — 74 px acima da
    // pegada, mais do dobro da altura de um avatar. Não é exagero, é o que a peça
    // é: um cartaz de estrada só se lê se estiver acima da cabeça de quem passa, e
    // a pegada só precisa de conter o que os postes ocupam no CHÃO, que é o tile e
    // meio de cada perna. Transbordar para cima é grátis (`setOrigin(0.5, 1)`);
    // transbordar para os LADOS é que traz parede invisível, e aqui a largura bate
    // certo ao pixel com os 3 tiles.
    //
    // Cresceu 26 px em altura na troca (era 96×80). O ficheiro do pacote tem os
    // postes inteiros a passar por trás do painel, em vez de o painel a flutuar
    // sobre dois tocos — a largura não mexeu, e por isso `space.ts` também não.
    alvo: propKey("parkSign"),
    fonte: CARTAZ,
    corte: { x: 0, y: 0, w: 96, h: 106 },
    w: 96,
    h: 106,
    raio: 0,
    espelho: false,
  },
  // ── A Copa: bancada + frigorífico ──
  //
  // As duas peças saem do MESMO ficheiro e são vizinhas no mapa (`counter` nas
  // colunas 24-27, `fridge` na 28), o que levanta a única pergunta interessante
  // deste par: reduzir as duas metades pelo MESMO factor, para o conjunto se ler
  // como uma cozinha corrida, ou cada uma pela SUA pegada?
  //
  // Ganha a pegada, e não por gosto. Ao factor da bancada (128/1319) o
  // frigorífico dava 34 px de largura sobre um tile de 32 — dois pixels a
  // transbordar de cada lado, que numa peça `solid` é parede invisível a roçar
  // quem passa. Cada uma à sua largura custa 7% de diferença de escala entre as
  // duas: o frigorífico fica 62 px de alto em vez dos 66 "verdadeiros", continua
  // a ser a peça mais alta da parede (a bancada tem 58) e continua a encostar-lhe
  // — que é tudo o que o olho verifica. Sete por cento invisíveis valem mais do
  // que dois pixels de colisão errada.
  //
  // Alturas acima da pegada (58 sobre 32, 62 sobre 32) transbordam para CIMA,
  // como no cartaz: é uma elevação de frente, tem de ter altura de parede.
  {
    alvo: propKey("counter"),
    fonte: COZ_BANCADA,
    corte: { x: 0, y: 0, w: 128, h: 58 },
    w: 128,
    h: 58,
    raio: 0,
    espelho: false,
  },
  {
    alvo: propKey("fridge"),
    fonte: COZ_FRIGORIFICO,
    corte: { x: 0, y: 0, w: 32, h: 62 },
    w: 32,
    h: 62,
    raio: 0,
    espelho: false,
  },
  // ── Os quatro carros ──
  //
  // O caso mais simples que este ficheiro tem, e vale a pena dizer porquê: a
  // arte é EXACTAMENTE a pegada. 64×128 px = 2×4 tiles, corte = ficheiro
  // inteiro, escala 1, `escalaNitida` verdadeira, cópia pixel a pixel sem
  // suavização nenhuma. Não há transbordo para cima (não é preciso: uma vista
  // de cima não tem telhado a assomar acima do chão que ocupa) nem para os
  // lados (que é o que traria parede invisível).
  //
  // Nenhum leva `espelho`. Espelhar um carro visto de cima trocaria o lado do
  // condutor — e num parque com quatro modelos distintos ninguém precisa da
  // variedade barata que o espelho dá noutras peças.
  {
    alvo: propKey("carSport"),
    fonte: CARRO_DESP,
    corte: { x: 0, y: 0, w: 64, h: 128 },
    w: 64,
    h: 128,
    raio: 0,
    espelho: false,
  },
  {
    alvo: propKey("carSedan"),
    fonte: CARRO_SEDAN,
    corte: { x: 0, y: 0, w: 64, h: 128 },
    w: 64,
    h: 128,
    raio: 0,
    espelho: false,
  },
  {
    alvo: propKey("carSuper"),
    fonte: CARRO_SUPER,
    corte: { x: 0, y: 0, w: 64, h: 128 },
    w: 64,
    h: 128,
    raio: 0,
    espelho: false,
  },
  {
    alvo: propKey("carSuv"),
    fonte: CARRO_SUV,
    corte: { x: 0, y: 0, w: 64, h: 128 },
    w: 64,
    h: 128,
    raio: 0,
    espelho: false,
  },
  // Os três objectos de cenário. `corte` é o ficheiro INTEIRO e igual a `w × h`:
  // foram assados em offline já na medida de ecrã (a pegada de `PROP_SPECS`
  // vezes `TILE`), logo o `recortar` copia-os pixel a pixel e o `escalaNitida`
  // devolve 1. A caçamba e a lixeira enchem a pegada à largura ao pixel; o cone
  // usa 27 dos 32 porque manter a proporção do render manda nisso — e o que
  // sobra em altura sobe, que é para onde a arte transborda de graça.
  {
    alvo: propKey("dumpster"),
    fonte: CACAMBA,
    corte: { x: 0, y: 0, w: 64, h: 56 },
    w: 64,
    h: 56,
    raio: 0,
    espelho: false,
  },
  {
    alvo: propKey("trashCan"),
    fonte: LIXEIRA,
    corte: { x: 0, y: 0, w: 32, h: 48 },
    w: 32,
    h: 48,
    raio: 0,
    espelho: false,
  },
  {
    alvo: propKey("cone"),
    fonte: CONE,
    corte: { x: 0, y: 0, w: 32, h: 40 },
    w: 32,
    h: 40,
    raio: 0,
    espelho: false,
  },
  {
    // Relógio de pé: pegada 1×1, arte 24×68. Começou a 20×56 (2,04 m à escala do
    // boneco) e cresceu a pedido. 24×68 é o degrau que bate a proporção da caixa
    // opaca do render AO PIXEL (354×1003 = 0,3529; 24/68 = 0,3529) e ainda deixa
    // 4 px de cada lado dentro do tile. Fica maior do que um relógio a sério
    // (2,5 m aos 27,4 px/m do avatar) — é de propósito, para se ler na sala.
    //
    // Ao trocar este PNG mexer TAMBÉM em `PROP_DRAWERS.grandfatherClock` (12×34 · 2).
    alvo: propKey("grandfatherClock"),
    fonte: RELOGIO,
    corte: { x: 0, y: 0, w: 24, h: 68 },
    w: 24,
    h: 68,
    raio: 0,
    espelho: false,
  },
];

/**
 * Cenas cujos PNGs falharam a carregar, por chave de origem.
 *
 * Existe por causa de uma armadilha que já custou uma sessão: um 404 no
 * `load.image` NÃO dá erro — o Phaser mete a textura `__MISSING` (um quadrado
 * preto com contorno verde) e a cena desenha-se toda, errada e calada. Aqui,
 * uma chave que falhou é simplesmente saltada: o `drawMeetingTable` e o
 * `drawDeskDual` de `officeArt.ts` voltam a pintar os móveis, a sala fica com
 * ar de v1, e ninguém vê quadrados pretos.
 *
 * `WeakMap` e não um `Set` global porque cada montagem da página cria uma cena
 * nova; assim o registo morre com ela em vez de se acumular.
 */
const falhadas = new WeakMap<PhaserT.Scene, Set<string>>();

/**
 * A escala é um múltiplo inteiro do original em AMBOS os eixos?
 *
 * Decide entre nitidez e suavização, e num mundo `pixelArt: true` a escolha
 * vê-se logo. Numa AMPLIAÇÃO inteira (2×, 3×) a resposta certa é
 * vizinho-mais-próximo: cada pixel do original vira um bloco quadrado e o
 * resultado fica com a mesma granulometria do resto do escritório, que é pintado
 * em blocos de 2 px. Suavizada, essa mesma ampliação dava um borrão de gradientes
 * no meio de arte de pixels.
 *
 * A maioria das peças REDUZ por um factor quebrado: a mesa a 0,706×, a consola a
 * 0,75×, o bastidor a 0,84×, a máquina a 0,64×, o painel a 0,857×, a mobile home
 * `v1` a 0,542× (a maior redução da lista, e a mais perto do chão). Num factor
 * quebrado não há bloco certo — o vizinho-mais-próximo deitava umas colunas fora
 * e outras não, e o contorno preto do tampo ficava ora com 1 px ora com nenhum.
 * A suavização é aqui o menor dos males, e a três quartos é um reamostrar leve,
 * não um desfoque.
 *
 * As duas casas novas (`Side` e `Arch`) são a excepção e caem do lado nítido: os
 * ficheiros foram preparados já no tamanho de ecrã, logo `kx = ky = 1`, inteiro,
 * e a cópia é pixel a pixel. Escala 1 é o caso ideal e não um acaso — nada se
 * reamostra, nada se perde, e o ficheiro ainda fica mais pequeno do que o render
 * grande equivalente. Quando houver de novo arte de origem para tratar, é este o
 * alvo: sair do tratamento com a medida final, não com o dobro dela.
 *
 * A função fica na mesma, e não por simetria: é ela que impede que a próxima peça
 * a entrar — se ampliar 2× — seja borratada em silêncio.
 *
 * Pura de propósito: é a única decisão do módulo que se testa sem browser.
 */
export function escalaNitida(sw: number, sh: number, dw: number, dh: number): boolean {
  if (sw <= 0 || sh <= 0) return false;
  const kx = dw / sw;
  const ky = dh / sh;
  return kx === ky && Number.isInteger(kx) && kx >= 1;
}

/**
 * Este pixel cai FORA do rectângulo de cantos arredondados?
 *
 * A conta é a distância do centro do pixel ao centro do canto mais próximo, e
 * só conta nos cantos: `qx`/`qy` valem zero em todo o miolo e nos lados, por
 * isso o teste só reprova nas quatro esquinas. É o mesmo cálculo que a
 * simulação offline correu antes de eu escolher o raio — se aqui divergisse,
 * o que eu vi não seria o que o browser desenha.
 */
export function foraDoCanto(x: number, y: number, w: number, h: number, raio: number): boolean {
  if (raio <= 0) return false;
  const px = x + 0.5;
  const py = y + 0.5;
  const qx = Math.max(raio - px, px - (w - raio), 0);
  const qy = Math.max(raio - py, py - (h - raio), 0);
  return qx * qx + qy * qy > raio * raio;
}

/**
 * Recorta, redimensiona e arredonda os cantos.
 *
 * Nenhuma peça reduz MUITO — as que reduzem ficam entre 0,54× e 0,86×, todas acima
 * de metade, e duas não reduzem de todo — e é por isso que aqui não há o ciclo de
 * metades que o `officeFlora.ts` tem. Esse existe para reduzir MUITO sem esfarelar:
 * um `drawImage` directo de 292 px para 112 salta pixels e a folhagem fica com
 * buracos. Acima de 0,5× cada pixel do destino ainda encontra vizinhos no original,
 * e a suavização resolve. Se algum dia entrar aqui uma peça que reduza a menos de
 * metade, é de lá que se traz a técnica — não se deixa o `drawImage` directo e pronto.
 *
 * A margem é mais estreita do que parece para quem chega em tamanho de render: a
 * mobile home `v1` está a 0,542×, a 8% do limite, e uma casa que quisesse uma
 * pegada de 4 tiles (128/295 = 0,434×) já cairia do lado errado — o sinal seriam
 * os arbustos a esfarelar, não um erro. A maneira de nunca lá chegar é a das duas
 * casas seguintes: reduzir FORA do browser, uma vez, com filtro de caixa e alfa
 * pré-multiplicado, e guardar o ficheiro já na medida em que vai ser desenhado.
 *
 * Sem contexto 2D (jsdom nos testes) devolve o canvas vazio em vez de rebentar.
 */
function recortar(src: CanvasImageSource, peca: Peca): HTMLCanvasElement {
  const fim = document.createElement("canvas");
  fim.width = peca.w;
  fim.height = peca.h;
  const ctx = fim.getContext("2d", { willReadFrequently: peca.raio > 0 });
  if (!ctx) return fim;

  const nitido = escalaNitida(peca.corte.w, peca.corte.h, peca.w, peca.h);
  ctx.imageSmoothingEnabled = !nitido;
  if (!nitido) ctx.imageSmoothingQuality = "high";
  if (peca.espelho) {
    // Espelhar o CONTEXTO e não a imagem: o `drawImage` a seguir fica exactamente
    // igual, e é isso que garante que as duas orientações são o mesmo recorte,
    // a mesma escala e o mesmo tratamento de suavização — não duas peças parecidas.
    ctx.translate(peca.w, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(src, peca.corte.x, peca.corte.y, peca.corte.w, peca.corte.h, 0, 0, peca.w, peca.h);
  if (peca.espelho) ctx.setTransform(1, 0, 0, 1, 0, 0);

  if (peca.raio > 0) {
    // A máscara faz-se a apagar alfa pixel a pixel, e não com um
    // `destination-in` sobre um caminho arredondado, porque o caminho vem
    // suavizado: numa mesa de pixels os cantos ficavam com uma franja
    // semi-transparente cinzenta em vez do contorno preto que a arte já traz.
    const img = ctx.getImageData(0, 0, peca.w, peca.h);
    for (let y = 0; y < peca.h; y += 1) {
      for (let x = 0; x < peca.w; x += 1) {
        if (foraDoCanto(x, y, peca.w, peca.h, peca.raio)) img.data[(y * peca.w + x) * 4 + 3] = 0;
      }
    }
    ctx.putImageData(img, 0, 0);
  }
  return fim;
}

/**
 * Põe os PNGs na fila de carregamento e arma a rede de segurança.
 *
 * Tem de ser chamado no `preload` da cena: o `create` só corre depois de isto
 * acabar, e é essa garantia que permite ao `ensureFurnitureTextures` assumir que
 * as imagens ou estão prontas ou estão marcadas como falhadas.
 */
export function preloadOfficeFurniture(scene: PhaserT.Scene): void {
  const caidas = new Set<string>();
  falhadas.set(scene, caidas);
  scene.load.on("loaderror", (file: { key: string }) => {
    caidas.add(file.key);
  });
  for (const [chave, url] of Object.entries(FICHEIROS)) scene.load.image(chave, url);
}

/**
 * Regista os móveis do pacote sob as chaves que o mundo do v1 desenha.
 *
 * Corre no início do `drawWorld`, antes do `ensureWorldTextures` — invertida, a
 * ordem não faria nada: as versões desenhadas já lá estariam e o guarda de
 * `texture()` mandava esta arte embora sem se queixar.
 */
export function ensureFurnitureTextures(scene: PhaserT.Scene): void {
  const caidas = falhadas.get(scene) ?? new Set<string>();

  for (const peca of PECAS) {
    if (caidas.has(peca.fonte)) continue; // sem PNG: fica o desenho do v1
    if (scene.textures.exists(peca.alvo)) continue; // já registada nesta cena
    if (!scene.textures.exists(peca.fonte)) continue; // nem chegou a carregar

    const img = scene.textures.get(peca.fonte).getSourceImage() as HTMLImageElement;
    scene.textures.addCanvas(peca.alvo, recortar(img, peca));
  }
}
